import {AmenitiesEnum} from '../../components/amenities/types'
import {CancelPolicyEnum} from '../booking/types'
import {PromoApplied} from '../promo-codes/types'
import {ServiceTypesEnum} from '../service/types'
import {Media, OpeningTimes} from '../types'

export enum AmountByTypeEnum {
  ENTRIES = 'AMOUNT_BY_ENTRIES',
  MINUTES = 'AMOUNT_BY_MINUTES',
  CREDITS = 'AMOUNT_BY_CREDITS',
}

export enum PriceTypeEnum {
  FREE = 'FREE',
  BY_PERCENTAGE = 'BY_PERCENTAGE',
}

export enum FrequencyTypeEnum {
  YEAR = 'YEAR',
  MONTH = 'MONTH',
  IN_TOTAL = 'IN_TOTAL',
}

export enum ActivityGroupTypeEnum {
  ALL = 'all',
  CLASS = 'class',
  SERVICE = 'service',
  TEACHER = 'teacher',
  SALES = 'sales',
}

export type EntityType = {
  isAll?: boolean
  areaSpace?: boolean
  value: string
  sportType?: string
  profileImage?: Media
  name: string
}

export type SubscriptionActivityEntity = {
  name: string
  value: string
  sportType?: string
  areaSpace?: boolean
  profileImage?: Media
  isAll?: boolean
}

export type SubscriptionActivity = {
  uid: string
  groupType: 'all' | 'class' | 'service' | 'teacher'
  sportType: ServiceTypesEnum
  priceType: PriceTypeEnum
  discount?: number
  entities: SubscriptionActivityEntity[]
  unlimited: boolean
  cancelPolicy?: CancelPolicyEnum | null
  amountByType?: AmountByTypeEnum
  frequencyType?: FrequencyTypeEnum
  coverEquipment: boolean
  frequencyAmount: number
  initialDiscount?: number
  useForParticipants?: boolean
  initialDiscountEnabled?: boolean
  // false => the activity is only usable inside `openingTimes` (days + hours). Absent/true => any time.
  allTheTime?: boolean
  openingTimes?: OpeningTimes
}

export type CalculatedSubscriptionActivity = SubscriptionActivity & {
  usedLimitations: {
    entries: number
    credits: number
    minutes: number
  }
}

export enum SubscriptionDurationTypeEnum {
  WEEKS = 'WEEK',
  YEARS = 'YEAR',
  MONTHS = 'MONTH',
  DAYS = 'DAYS',
}

export enum SubscriptionPriceTypesEnum {
  ONE_TIME = 0,
  WEEKLY = 1,
  MONTHLY = 2,
  YEARLY = 3,
}

export type PriceOption = {
  cancelPolicy: string
  price: number
  type: SubscriptionPriceTypesEnum
  maxInstallments: number
  additionalFee: number
  initialPrice?: number
  discount?: number
  // Present when the plain endpoint was asked with ?promoCode= (contract D364 §3.3).
  listPrice?: number
  firstPaymentPrice?: number
  promoDiscount?: number
  // Only with ?firstCyclePrice=: that first cycle (at list) and the code's discount on it.
  firstCyclePrice?: number
  firstCyclePromoDiscount?: number
}

export type PurchaseLimitUnit = 'DAY' | 'MONTH' | 'WEEK' | 'YEAR'

// null/absent => no limit. ONCE => once per person, ever. PER_PERIOD => `max` per `unit`.
export type PurchaseLimit = {type: 'ONCE'} | {type: 'PER_PERIOD'; max: number; unit: PurchaseLimitUnit}

// A pass the person owns that cannot be used for the chosen time (validate/byUser with includeUnavailable).
export type UnavailableSubscription = {
  optionIndex: number
  membershipId: string
  membershipBookingId: string
  name: string
  logo?: {secure_url: string} | null
  reason: 'OUTSIDE_PASS_HOURS'
  openingTimes?: OpeningTimes
}

export type Subscription = {
  uid: string
  membershipId: string
  activityGroupId: string
  disabledFromAssessConfig?: boolean
  reference: string
  status: 'ACTIVE' | 'INACTIVE'
  subStatus?: 'FROZEN'
  logo: {
    secure_url: string
  }
  useForParticipants?: boolean
  membershipName: string
  membershipBookingId: string
  membershipBookingReference: string
  coverEquipment: boolean
  cancelPolicy: CancelPolicyEnum | null
  prices: PriceOption[]
  activities: SubscriptionActivity[]
  rolling: boolean
  fromDateOfPurchase: boolean
  fromDateOfPurchaseAfterStartDate: boolean
  startDate: string
  duration: number
  durationType: SubscriptionDurationTypeEnum
  venueId: string
  startDateType: 'PURCHASE_DATE' | 'SPECIFIC_DATE' | 'USER_SELECTED_DATE'
  maxStartDaysUnlimited?: boolean
  maxStartDaysAmount?: number
  purchaseLimit?: PurchaseLimit | null
}

export type SubscriptionData = {
  uid: string
  name: string
  logo: {secure_url: string}
  prices: PriceOption[]
  rolling: boolean
  venueId: string
  duration: number
  startDate: string
  amenities?: AmenitiesEnum[]
  activities: SubscriptionActivity[]
  description: string
  purchaseLimit?: PurchaseLimit | null
  durationType: SubscriptionDurationTypeEnum
  cancelPolicy: CancelPolicyEnum | null
  startDateType: 'PURCHASE_DATE' | 'SPECIFIC_DATE' | 'USER_SELECTED_DATE'
  byRequestOnly?: boolean
  amenitiesDiffer: boolean
  fromDateOfPurchase: boolean
  termsAndConditions?: string
  maxStartDaysAmount?: number
  maxStartDaysUnlimited?: boolean
  fromDateOfPurchaseAfterStartDate: boolean
  promoApplied?: PromoApplied | null
  permissionData: {
    show: boolean
    validated: boolean
    reason?: 'FOR_MEMBERS_ONLY' | 'FOR_NON_MEMBERS_ONLY' | 'NOT_AVAILABLE'
  }
  requestStatus?:
    | 'ACCEPT'
    | 'AWAITING_MEMBER_AUTHORISATION'
    | 'AWAITING_VENUE_AUTHORISATION'
    | 'DECLINE_BY_USER'
    | 'DECLINE_BY_VENUE'
    | 'DECLINE'
    | null
}

export type CalculatedSubscription = Subscription & {
  activities: CalculatedSubscriptionActivity[]
}
